import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus, RefreshCw, UserX } from "lucide-react";

/**
 * Delegação de ocorrência (FASES 8-9).
 *
 * A lista de técnicos é fornecida pelo backend (GET /occurrences/{id}/eligible-technicians),
 * que aplica o Authorization Engine — o frontend apenas exibe o resultado.
 * O backend valida novamente no POST /occurrences/{id}/assign.
 */
export default function OccurrenceAssignDialog({ open, onOpenChange, occurrence, onDone }) {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [technicianId, setTechnicianId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const occId = occurrence?.id;

  const load = async () => {
    if (!occId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/occurrences/${occId}/eligible-technicians`);
      setTechnicians(data.technicians || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setTechnicians([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setTechnicianId("");
      setReason("");
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, occId]);

  const assign = async () => {
    if (!technicianId) return;
    setBusy(true);
    try {
      await api.post(`/occurrences/${occId}/assign`, {
        technician_id: technicianId,
        reason: reason || undefined,
      });
      toast.success("Ocorrência delegada — o técnico foi notificado");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const unassign = async () => {
    setBusy(true);
    try {
      await api.delete(`/occurrences/${occId}/assignment`);
      toast.success("Delegação removida");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus size={17} className="text-primary" /> Delegar ocorrência
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {occurrence?.protocol && (
            <p className="text-sm font-medium" data-testid="assign-protocol">{occurrence.protocol}</p>
          )}
          {occurrence?.assigned_to_name && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                Técnico delegado atual: <span className="font-medium text-foreground">{occurrence.assigned_to_name}</span>
              </p>
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={unassign}
                data-testid="assign-remove-btn">
                <UserX size={12} className="mr-1" /> Remover
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Técnicos elegíveis (validados no servidor)</p>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={load} disabled={loading}
              data-testid="assign-refresh-btn" title="Recarregar lista">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>

          {loading ? (
            <p className="text-xs text-muted-foreground py-3">Carregando técnicos...</p>
          ) : technicians.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3" data-testid="assign-empty">
              Nenhum técnico elegível para esta ocorrência (escopo/permissões).
            </p>
          ) : (
            <Select value={technicianId} onValueChange={setTechnicianId}>
              <SelectTrigger data-testid="assign-technician-select"><SelectValue placeholder="Selecione o técnico" /></SelectTrigger>
              <SelectContent>
                {technicians.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`assign-tech-${t.id}`}>
                    {t.name}{t.email ? ` · ${t.email}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="resize-none"
            placeholder="Motivo da delegação (opcional)" data-testid="assign-reason-input" />

          <Button className="w-full" onClick={assign} disabled={busy || !technicianId} data-testid="assign-submit-btn">
            {busy ? "Delegando..." : "Confirmar delegação"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
